import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Accordion, Form, Button, Row, Col, Image, Spinner, Tabs, Tab, InputGroup, Card, Alert, Badge, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useEntidadeContext } from '../../EntidadeContext';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const LandPageServicosTab = ({ initialServicos = [], isLoading }) => {
    const { currentEntidade } = useEntidadeContext(); // Obter a entidade atual
    const [configs, setConfigs] = useState({});
    const [isSaving, setIsSaving] = useState({}); // Controla o estado de salvamento por serviço

    // Estados para o modal de corte
    const [showCropper, setShowCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [aspect, setAspect] = useState(16 / 9);
    const [cropContext, setCropContext] = useState(null); // { servicoId, blockId, field }
    const imgRef = useRef(null);

    useEffect(() => {
        const initialData = {};
        initialServicos.forEach(servico => {
            const marketingConfig = servico.marketing_config;

            // Prepara os dados de "Antes e Depois"
            const initialImagensAntesDepois = (marketingConfig?.imagens_antes_depois || []).map((img, index) => ({
                id: img.id || Date.now() + index, // Usa o ID existente ou um temporário
                antes: { file: null, preview: img.antes_path ? `/storage/${img.antes_path}` : null },
                depois: { file: null, preview: img.depois_path ? `/storage/${img.depois_path}` : null },
            }));

            // NOVO: Processa os content_blocks para adicionar a URL de preview
            const processedContentBlocks = (marketingConfig?.content_blocks || []).map(block => {
                if (block.type === 'image') {
                    const newBlock = { ...block };
                    if (block.src_desktop?.path) {
                        newBlock.src_desktop.preview = `/storage/${block.src_desktop.path}`;
                    }
                    if (block.src_mobile?.path) {
                        newBlock.src_mobile.preview = `/storage/${block.src_mobile.path}`;
                    }
                    return newBlock;
                }
                // NOVO: Garante que blocos de formulário tenham valores padrão
                if (block.type === 'form') {
                    return {
                        ...block,
                        titulo: block.titulo || 'Comece sua transformação',
                        descricao: block.descricao || 'Preencha o formulário para agendar sua avaliação.'
                    };
                }
                return block;
            });
            // Estrutura de dados baseada na sugestão de alta conversão
            initialData[servico.id] = {
                // --- Bloco Principal (Hero) ---
                titulo_hero: marketingConfig?.titulo_hero || ``,
                subtitulo_hero: marketingConfig?.subtitulo_hero || '',
                texto_cta_principal: marketingConfig?.texto_cta_principal || '',
                redirect_url: marketingConfig?.redirect_url || '', // NOVO CAMPO
                imagem_hero_preview: marketingConfig?.imagem_hero_path ? `/storage/${marketingConfig.imagem_hero_path}` : null,
                
                // --- Bloco de Benefícios ---
                titulo_beneficios: marketingConfig?.titulo_beneficios || '',
                lista_beneficios: marketingConfig?.lista_beneficios || [],

                // --- Bloco de FAQ ---
                titulo_faq: marketingConfig?.titulo_faq || '',
                lista_faq: marketingConfig?.lista_faq || [],

                // --- Bloco de Prova Social (Antes e Depois) ---
                titulo_antes_depois: marketingConfig?.titulo_antes_depois || '',
                lista_antes_depois: initialImagensAntesDepois,

                // --- Bloco de Oferta e Urgência ---
                titulo_oferta: marketingConfig?.titulo_oferta || '',
                preco_antigo: marketingConfig?.preco_antigo || '',
                preco_novo: marketingConfig?.preco_novo || '',
                texto_urgencia: marketingConfig?.texto_urgencia || '',

                // --- Bloco de SEO e Metadados ---
                titulo_pagina: marketingConfig?.titulo_pagina || '',
                meta_descricao: marketingConfig?.meta_descricao || '',
                template_slug: 'video_hero', // Força o template 'video_hero' como padrão

                // --- Estrutura de Blocos Dinâmicos ---
                content_blocks: processedContentBlocks,

                file: null,
            };
        });
        setConfigs(initialData);
    }, [initialServicos]);

    const handleChange = (servicoId, field, value) => {
        setConfigs(prev => ({
            ...prev,
            [servicoId]: {
                ...prev[servicoId],
                [field]: value,
            },
        }));
    };

    // --- Funções para gerenciar listas dinâmicas (Benefícios e FAQ) ---

    const handleListChange = (servicoId, listName, index, field, value) => {
        const newList = [...configs[servicoId][listName]];
        newList[index][field] = value;
        handleChange(servicoId, listName, newList);
    };

    const addListItem = (servicoId, listName) => {
        const newItem = listName === 'lista_beneficios' 
            ? { icone: '✅', titulo: '', descricao: '' }
            : { pergunta: '', resposta: '' };
        
        const newList = [...configs[servicoId][listName], newItem];
        handleChange(servicoId, listName, newList);
    };

    const removeListItem = (servicoId, listName, index) => {
        const newList = configs[servicoId][listName].filter((_, i) => i !== index);
        handleChange(servicoId, listName, newList);
    };

    const handleImagePairChange = (servicoId, pairId, side, file) => {
        if (!file) return;

        const newList = configs[servicoId].lista_antes_depois.map(pair => {
            if (pair.id === pairId) {
                return {
                    ...pair,
                    [side]: { file: file, preview: URL.createObjectURL(file) }
                };
            }
            return pair;
        });
        handleChange(servicoId, 'lista_antes_depois', newList);
    };

    const addImagePair = (servicoId) => {
        const newPair = { id: Date.now(), antes: { file: null, preview: null }, depois: { file: null, preview: null } };
        handleChange(servicoId, 'lista_antes_depois', [...configs[servicoId].lista_antes_depois, newPair]);
    };

    const removeImagePair = (servicoId, pairId) => {
        const newList = configs[servicoId].lista_antes_depois.filter(pair => pair.id !== pairId);
        handleChange(servicoId, 'lista_antes_depois', newList);
    };

    const handleFileChange = (servicoId, e) => {
        const file = e.target.files[0];
        if (file) {
            setConfigs(prev => ({
                ...prev,
                [servicoId]: {
                    ...prev[servicoId],
                    file: file,
                    imagem_hero_preview: URL.createObjectURL(file),
                },
            }));
        }
    };

    // --- Funções para gerenciar a estrutura de blocos ---

    const handleBlockChange = (servicoId, blockId, field, value) => {
        const newBlocks = configs[servicoId].content_blocks.map(block => {
            if (block.id === blockId) {
                return { ...block, [field]: value };
            }
            return block;
        });
        handleChange(servicoId, 'content_blocks', newBlocks);
    };

    // // Handler para abrir o modal de corte de imagem
    // const handleOpenCropper = (servicoId, blockId, field, file) => {
    //     if (!file) return;

    //     const newBlocks = configs[servicoId].content_blocks.map(block => {
    //         if (block.id === blockId) {
    //             // Cria um novo objeto para o campo (ex: src_desktop) com o arquivo e a URL de preview
    //             const newFieldValue = { file: file, preview: URL.createObjectURL(file) };
    //             return { ...block, [field]: newFieldValue };
    //         }
    //         return block;
    //     });
    //     handleChange(servicoId, 'content_blocks', newBlocks);
    // };
 
    // Handler para quando o corte é finalizado (memoizado com useCallback)
    const handleCropComplete = useCallback(async () => {
        if (!completedCrop || !imgRef.current || !cropContext) return;

        const { servicoId, blockId, field, originalFile } = cropContext;
        const canvas = document.createElement('canvas');
        const image = imgRef.current;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        // Define as dimensões do canvas para o tamanho real do corte na imagem original.
        canvas.width = completedCrop.width * scaleX;
        canvas.height = completedCrop.height * scaleY;

        const ctx = canvas.getContext('2d');

        // Desenha a área cortada da imagem original no canvas.
        ctx.drawImage(
            image,
            completedCrop.x * scaleX, // Ponto X inicial na imagem original
            completedCrop.y * scaleY, // Ponto Y inicial na imagem original
            completedCrop.width * scaleX, // Largura do corte na imagem original
            completedCrop.height * scaleY, // Altura do corte na imagem original
            0, 0, // Ponto (0,0) no canvas
            canvas.width, // Largura final no canvas
            canvas.height // Altura final no canvas
        );

        canvas.toBlob(blob => {
            if (!blob) {
                console.error('Canvas to Blob conversion failed');
                return;
            }
            const croppedFile = new File([blob], originalFile.name, { type: originalFile.type });
            const newBlocks = configs[servicoId].content_blocks.map(block => {
                if (block.id === blockId) {
                    const newFieldValue = { file: croppedFile, preview: URL.createObjectURL(croppedFile) };
                    return { ...block, [field]: newFieldValue };
                }
                return block;
            });
            handleChange(servicoId, 'content_blocks', newBlocks);
            setShowCropper(false);
            setImageToCrop(null);
        }, originalFile.type, 0.95); // Qualidade da imagem
    }, [completedCrop, imgRef, cropContext, configs, handleChange]); // Dependências do useCallback

    // Handler para abrir o modal de corte de imagem (memoizado com useCallback)
    const handleOpenCropper = useCallback((servicoId, blockId, field, file) => {
        if (!file) return;
        const newAspect = field === 'src_desktop' ? 1430 / 700 : 640 / 1024; // Define o aspect ratio
        setAspect(newAspect);
        setImageToCrop(URL.createObjectURL(file));
        setCropContext({ servicoId, blockId, field, originalFile: file });
        setShowCropper(true);
    }, [setAspect, setImageToCrop, setCropContext, setShowCropper]); // Dependências do useCallback
    
    // Handler para quando a imagem do cropper é carregada
    const onImageLoad = (e) => {
        const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
        const crop = centerCrop(
            makeAspectCrop(
                {
                    unit: '%',
                    width: 90,
                },
                aspect, width, height
            ), width, height);
        setCrop(crop);
    };

    const addContentBlock = (servicoId, type) => {
        const currentBlocks = configs[servicoId].content_blocks;
        const blockLimits = {
            image: 10,
            video: 3,
            form: 2,
        };

        if (blockLimits[type]) {
            const count = currentBlocks.filter(block => block.type === type).length;
            if (count >= blockLimits[type]) {
                // Apenas uma segurança extra, o botão já deve estar desabilitado.
                return;
            }
        }
        const newBlock = {
            id: `block-${Date.now()}`,
            type: type,
            // Ajustado para a nova estrutura de imagem
            src_desktop: type === 'image' ? { file: null, preview: 'https://placehold.co/120x80/EFEFEF/333?text=1430x700' } : undefined,
            src_mobile: type === 'image' ? { file: null, preview: 'https://placehold.co/88x132/EFEFEF/333?text=640x1024' } : undefined,
            src: type === 'video' ? '' : undefined,
            formId: type === 'form' ? `form-${Date.now()}` : undefined,
            // Para os novos tipos, não precisamos de dados extras, apenas o 'type'
            // NOVO: Adiciona campos padrão para o formulário
            titulo: type === 'form' ? 'Comece sua transformação' : undefined,
            descricao: type === 'form' ? 'Preencha o formulário para agendar sua avaliação.' : undefined,
            sectionBgColor: '#FFFFFF',
        };
        const newBlocks = [...configs[servicoId].content_blocks, newBlock];
        handleChange(servicoId, 'content_blocks', newBlocks);
    };

    const removeContentBlock = (servicoId, blockId) => {
        const newBlocks = configs[servicoId].content_blocks.filter(block => block.id !== blockId);
        handleChange(servicoId, 'content_blocks', newBlocks);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const servicoId = result.destination.droppableId;

        const items = Array.from(configs[servicoId].content_blocks);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        handleChange(servicoId, 'content_blocks', items);
    };




    const handleSave = async (servicoId) => {
        setIsSaving(prev => ({ ...prev, [servicoId]: true }));

        const formData = new FormData();
        const config = configs[servicoId];

        // Contar imagens e vídeos
        const quantidadeImagens = config.content_blocks.filter(block => block.type === 'image').length;
        const quantidadeVideos = config.content_blocks.filter(block => block.type === 'video').length;

        // Adiciona os arquivos de imagem (desktop/mobile) se existirem
        config.content_blocks.forEach((block, index) => {
            if (block.type === 'image') {
                if (block.src_desktop?.file) formData.append(`content_block_files[${index}][src_desktop_file]`, block.src_desktop.file);
                if (block.src_mobile?.file) formData.append(`content_block_files[${index}][src_mobile_file]`, block.src_mobile.file);
            }
        });

        // Adicionar todos os outros campos ao formData
        formData.append('titulo_hero', config.titulo_hero);
        formData.append('subtitulo_hero', config.subtitulo_hero);
        formData.append('texto_cta_principal', config.texto_cta_principal);
        formData.append('redirect_url', config.redirect_url); // NOVO CAMPO
        formData.append('lista_beneficios', JSON.stringify(config.lista_beneficios));
        formData.append('titulo_beneficios', config.titulo_beneficios);
        formData.append('lista_faq', JSON.stringify(config.lista_faq));
        formData.append('titulo_faq', config.titulo_faq);
        formData.append('titulo_oferta', config.titulo_oferta);
        formData.append('preco_antigo', config.preco_antigo);
        formData.append('preco_novo', config.preco_novo);
        formData.append('texto_urgencia', config.texto_urgencia);
        formData.append('titulo_pagina', config.titulo_pagina);
        formData.append('meta_descricao', config.meta_descricao);
        formData.append('template_slug', config.template_slug); // Enviar o slug do template
        formData.append('titulo_antes_depois', config.titulo_antes_depois);        
        formData.append('quantidade_imagens', quantidadeImagens); // Contagem de imagens
        formData.append('content_blocks', JSON.stringify(config.content_blocks)); // Enviar a estrutura de blocos

        // Adiciona a imagem principal (Hero) se ela foi alterada
        if (config.file) {
            formData.append('imagem_hero', config.file);
        }

        // Adiciona os pares de imagens "Antes e Depois"
        const existingImagePairs = [];
        config.lista_antes_depois.forEach((pair, index) => {
            // Se o ID não for um número (ex: um timestamp de Date.now()), é um novo par.
            const isNewPair = typeof pair.id !== 'number' || pair.id > 1000000000;

            if (pair.antes.file) {
                formData.append(`imagens_antes_depois[${index}][antes_file]`, pair.antes.file);
            }
            if (pair.depois.file) {
                formData.append(`imagens_antes_depois[${index}][depois_file]`, pair.depois.file);
            }
            // Adiciona o ID apenas para pares existentes para que o backend possa identificá-los
            if (!isNewPair) {
                formData.append(`imagens_antes_depois[${index}][id]`, pair.id);
            }
        });

        try {
            await axios.post(`/api/servicos/${servicoId}/marketing-config`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            alert(`Configuração da Landing Page salva com sucesso!`);

        } catch (error) {
            console.error(`Erro ao salvar configuração do serviço ${servicoId}:`, error);
            alert(`Erro ao salvar configuração. (Simulação)`);
        } finally {
            setIsSaving(prev => ({ ...prev, [servicoId]: false }));
        }
    };

    if (isLoading) {
        return <div className="text-center"><Spinner animation="border" /> Carregando serviços...</div>;
    }

    const handleViewPage = (e, servico) => {
        e.stopPropagation(); // Impede que o accordion abra/feche ao clicar no botão
        if (currentEntidade && currentEntidade.slug && servico.slug) {
            const url = `/lp/${currentEntidade.slug}/${servico.slug}`;
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            alert('Não foi possível determinar a URL da página. Verifique se o serviço e a entidade têm um slug definido.');
        }
    };


    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Accordion>
                {initialServicos.filter(s => s.ativo === 1).map(servico => (
                    configs[servico.id] && <Accordion.Item eventKey={servico.id} key={servico.id}>
                        <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100">
                                <span>{servico.nome}</span>
                                {servico.marketing_config && (
                                    <Button variant="outline-secondary" size="sm" className="ms-3" onClick={(e) => handleViewPage(e, servico)}>
                                        <FontAwesomeIcon icon="eye" className="me-1" /> Visualizar Página
                                    </Button>
                                )}
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <ServicoTabs
                                servicoId={servico.id}
                                config={configs[servico.id]}
                                handleChange={handleChange}
                                handleFileChange={handleFileChange}
                                handleListChange={handleListChange}
                                addListItem={addListItem}
                                removeListItem={removeListItem}
                                handleImagePairChange={handleImagePairChange}
                                addImagePair={addImagePair}
                                removeImagePair={removeImagePair}
                                addContentBlock={addContentBlock}
                                removeContentBlock={removeContentBlock}
                                handleBlockChange={handleBlockChange}
                                handleOpenCropper={handleOpenCropper}
                            />
                            <hr />
                            <div className="text-end mt-3">
                                <Button variant="primary" onClick={() => handleSave(servico.id)} disabled={isSaving[servico.id] || false}>
                                    {(isSaving[servico.id] || false) ? (
                                        <><Spinner as="span" size="sm" animation="border" className="me-2" /> Salvando...</>
                                    ) : (
                                        <><FontAwesomeIcon icon="save" className="me-2" /> Salvar Configurações</>
                                    )}
                                </Button>
                            </div>
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>

            {/* Modal de Corte de Imagem */}
            <Modal show={showCropper} onHide={() => setShowCropper(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Cortar Imagem</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {imageToCrop && (
                        <ReactCrop crop={crop} onChange={c => setCrop(c)} onComplete={c => setCompletedCrop(c)} aspect={aspect}>
                            <img ref={imgRef} src={imageToCrop} onLoad={onImageLoad} alt="Crop me" style={{ maxHeight: '70vh' }} />
                        </ReactCrop>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCropper(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleCropComplete}>
                        Aplicar Corte
                    </Button>
                </Modal.Footer>
            </Modal>
        </DragDropContext>
    );
};

const ServicoTabs = ({
    servicoId,
    config,
    handleChange,
    handleFileChange,
    handleListChange,
    addListItem,
    removeListItem,
    handleImagePairChange,
    addImagePair,
    removeImagePair,    
    addContentBlock,
    removeContentBlock,
    handleBlockChange,
    handleOpenCropper
}) => {
    // Como agora o template é sempre 'video_hero', essa variável será sempre true.
    const isVideoHeroTemplate = true;
    const activeBlockTypes = new Set(config.content_blocks.map(block => block.type));

    const renderAddBlockButton = (type, icon, label) => {
        const uniqueBlockTypes = ['conteudo_principal', 'beneficios', 'faq', 'antes_depois', 'oferta', 'seo'];
        const limitedBlockTypes = {
            image: 10,
            video: 3,
            form: 2,
        };

        let isDisabled = false;
        let tooltipMessage = 'Este bloco já foi adicionado.';

        if (uniqueBlockTypes.includes(type)) {
            isDisabled = activeBlockTypes.has(type);
        } else if (limitedBlockTypes[type]) {
            const count = config.content_blocks.filter(block => block.type === type).length;
            isDisabled = count >= limitedBlockTypes[type];
            tooltipMessage = `Limite de ${limitedBlockTypes[type]} blocos atingido.`;
        }

        const button = (
            <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => !isDisabled && addContentBlock(servicoId, type)} disabled={isDisabled} style={isDisabled ? { pointerEvents: 'none' } : {}}>
                <FontAwesomeIcon icon={icon} className="me-2" /> {label}
            </Button>
        );

        if (isDisabled) {
            return (
                <OverlayTrigger overlay={<Tooltip>{tooltipMessage}</Tooltip>}>
                    <span className="d-inline-block">{button}</span>
                </OverlayTrigger>
            );
        }

        return button;
    };

    return (
        <Tabs defaultActiveKey="estrutura" id={`config-tabs-${servicoId}`} className="mb-3">
                <Tab eventKey="estrutura" title="🏗️ Estrutura da Página">
                    <Alert variant="info">
                        <FontAwesomeIcon icon="info-circle" className="me-2" />
                        Arraste os blocos para reordenar a estrutura da sua Landing Page.
                    </Alert>
                    <Droppable droppableId={String(servicoId)}>
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                {config?.content_blocks?.map((block, index) => (
                                    <Draggable key={block.id} draggableId={String(block.id)} index={index}>
                                        {(provided) => (
                                            <div ref={provided.innerRef} {...provided.draggableProps} className="mb-2">
                                                <Card>
                                                    <Card.Body>
                                                        <Row className="align-items-center">
                                                            <Col xs="auto" {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                                                                <FontAwesomeIcon icon="grip-vertical" />
                                                            </Col>
                                                            <Col>
                                                                {block.type === 'image' && (
                                                                    <>
                                                                        <Badge bg="secondary" className="mb-2">Bloco de Imagem</Badge>
                                                                        <Row>
                                                                            <Col md={2}>
                                                                                <Form.Label>Desktop</Form.Label>
                                                                                <Image src={block.src_desktop?.preview || 'https://placehold.co/120x80/EFEFEF/333?text=1200x800'} fluid thumbnail />
                                                                                <Form.Control className="mt-1" size="sm" type="file" accept="image/*" onChange={(e) => handleOpenCropper(servicoId, block.id, 'src_desktop', e.target.files[0])} />
                                                                            </Col>
                                                                        </Row>
                                                                        <Row>
                                                                             <Col md={2}>
                                                                                <Form.Label>Mobile</Form.Label>
                                                                                <Image src={block.src_mobile?.preview || 'https://placehold.co/80x120/EFEFEF/333?text=400x600'} fluid thumbnail />
                                                                                <Form.Control className="mt-1" size="sm" type="file" accept="image/*" onChange={(e) => handleOpenCropper(servicoId, block.id, 'src_mobile', e.target.files[0])} />
                                                                            </Col>
                                                                        </Row>
                                                                    </>
                                                                )}
                                                                {block.type === 'video' && (
                                                                    <>
                                                                        <Badge bg="danger">Vídeo</Badge>
                                                                        <Form.Control className="mt-2" size="sm" type="text" placeholder="URL do Vídeo (YouTube, Vimeo)" value={block.src || ''} onChange={(e) => handleBlockChange(servicoId, block.id, 'src', e.target.value)} />
                                                                    </>
                                                                )}
                                                                {block.type === 'form' && (
                                                                    <>
                                                                        <Badge bg="primary">Formulário</Badge>
                                                                        <Form.Group className="mt-2">
                                                                            <Form.Label size="sm">Título do Formulário</Form.Label>
                                                                            <Form.Control size="sm" type="text" value={block.titulo || ''} onChange={(e) => handleBlockChange(servicoId, block.id, 'titulo', e.target.value)} />
                                                                        </Form.Group>
                                                                        <Form.Group className="mt-2">
                                                                            <Form.Label size="sm">Descrição do Formulário</Form.Label>
                                                                            <Form.Control 
                                                                                as="textarea"
                                                                                rows={2}
                                                                                size="sm" value={block.descricao || ''} onChange={(e) => handleBlockChange(servicoId, block.id, 'descricao', e.target.value)} 
                                                                            />
                                                                        </Form.Group>
                                                                    </>
                                                                )}
                                                                {block.type === 'faq' && <Badge bg="info">FAQ</Badge>}
                                                                {block.type === 'beneficios' && <Badge bg="success">Benefícios</Badge>}
                                                                {block.type === 'conteudo_principal' && <Badge bg="dark">Conteúdo Principal</Badge>}
                                                                {block.type === 'oferta' && <Badge bg="danger">Oferta</Badge>}
                                                                {block.type === 'antes_depois' && <Badge bg="warning">Antes e Depois</Badge>}
                                                                <Form.Group as={Row} className="mt-2 align-items-center">
                                                                    <Form.Label column sm="auto" className="mb-0">Cor de Fundo:</Form.Label>
                                                                    <Col sm="auto">
                                                                        <Form.Control type="color" value={block.sectionBgColor} onChange={(e) => handleBlockChange(servicoId, block.id, 'sectionBgColor', e.target.value)} style={{ width: '40px', height: '30px', padding: '0' }} />
                                                                    </Col>
                                                                    <Col>
                                                                        <Form.Control type="text" size="sm" value={block.sectionBgColor} onChange={(e) => handleBlockChange(servicoId, block.id, 'sectionBgColor', e.target.value)} />
                                                                    </Col>
                                                                </Form.Group>
                                                            </Col>
                                                            <Col xs="auto">
                                                                <Button variant="outline-danger" size="sm" onClick={() => removeContentBlock(servicoId, block.id)}>
                                                                    <FontAwesomeIcon icon="trash" />
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                    <div className="mt-3">
                        <span className="me-2">Adicionar Bloco:</span>
                        {renderAddBlockButton('image', 'image', 'Imagem')}
                        {renderAddBlockButton('video', 'video', 'Vídeo')}
                        {renderAddBlockButton('form', 'file-alt', 'Formulário')}
                        {renderAddBlockButton('conteudo_principal', 'star', 'Principal')}
                        {renderAddBlockButton('beneficios', 'check-circle', 'Benefícios')}
                        {renderAddBlockButton('faq', 'question-circle', 'FAQ')}
                        {renderAddBlockButton('antes_depois', 'images', 'Antes/Depois')}
                        {renderAddBlockButton('oferta', 'tag', 'Oferta')}
                        {renderAddBlockButton('seo', 'cog', 'SEO')}
                    </div>
                </Tab>
            {activeBlockTypes.has('conteudo_principal') && (
                <Tab eventKey="hero" title="🌟 Conteúdo Principal">
                    <Form>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3" controlId={`titulo_hero-${servicoId}`}><Form.Label>Título Principal (Hero)</Form.Label><Form.Control type="text" value={config.titulo_hero} onChange={(e) => handleChange(servicoId, 'titulo_hero', e.target.value)} /></Form.Group>
                                <Form.Group className="mb-3" controlId={`subtitulo_hero-${servicoId}`}><Form.Label>Subtítulo</Form.Label><Form.Control as="textarea" rows={3} value={config.subtitulo_hero} onChange={(e) => handleChange(servicoId, 'subtitulo_hero', e.target.value)} /></Form.Group>
                                <Form.Group className="mb-3" controlId={`cta-${servicoId}`}><Form.Label>Texto do Botão de Ação (CTA)</Form.Label><Form.Control type="text" value={config.texto_cta_principal} onChange={(e) => handleChange(servicoId, 'texto_cta_principal', e.target.value)} /></Form.Group>
                                <Form.Group className="mb-3" controlId={`redirect_url-${servicoId}`}><Form.Label>URL de Redirecionamento (Opcional)</Form.Label><Form.Control type="text" placeholder="https://..." value={config.redirect_url} onChange={(e) => handleChange(servicoId, 'redirect_url', e.target.value)} /><Form.Text muted>Se preenchido, o usuário será levado para esta URL após o envio. Prioridade sobre o WhatsApp.</Form.Text></Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId={`imagem_hero-${servicoId}`}>
                                    <Form.Label>Imagem Principal</Form.Label>
                                    <div className="text-center mb-2"><Image src={config.imagem_hero_preview || 'https://via.placeholder.com/300x200?text=Sem+Imagem'} thumbnail style={{ maxHeight: '150px', width: 'auto' }} /></div>
                                    <Form.Control type="file" accept="image/png, image/jpeg" onChange={(e) => handleFileChange(servicoId, e)} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Tab>
            )}
            {activeBlockTypes.has('beneficios') && (
                <Tab eventKey="beneficios" title="✅ Benefícios">
                    <Form.Group className="mb-3"><Form.Label>Título da Seção de Benefícios</Form.Label><Form.Control type="text" value={config.titulo_beneficios} onChange={(e) => handleChange(servicoId, 'titulo_beneficios', e.target.value)} /></Form.Group>
                    {config.lista_beneficios.map((item, index) => (
                        <Card key={index} className="mb-2">
                            <Card.Body>
                                <Row className="align-items-center">
                                    <Col xs="auto"><Form.Control style={{ width: '60px' }} type="text" placeholder="Ícone" value={item.icone} onChange={(e) => handleListChange(servicoId, 'lista_beneficios', index, 'icone', e.target.value)} /></Col>
                                    <Col>
                                        <Form.Control className="mb-1" type="text" placeholder="Título do Benefício" value={item.titulo} onChange={(e) => handleListChange(servicoId, 'lista_beneficios', index, 'titulo', e.target.value)} />
                                        <Form.Control size="sm" type="text" placeholder="Descrição curta" value={item.descricao} onChange={(e) => handleListChange(servicoId, 'lista_beneficios', index, 'descricao', e.target.value)} />
                                    </Col>
                                    <Col xs="auto"><Button variant="danger" size="sm" onClick={() => removeListItem(servicoId, 'lista_beneficios', index)}><FontAwesomeIcon icon="trash" /></Button></Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                    <Button variant="outline-primary" size="sm" onClick={() => addListItem(servicoId, 'lista_beneficios')}><FontAwesomeIcon icon="plus" className="me-2" /> Adicionar Benefício</Button>
                </Tab>
            )}
            {activeBlockTypes.has('faq') && (
                <Tab eventKey="faq" title="❓ FAQ">
                    <Form.Group className="mb-3"><Form.Label>Título da Seção de Dúvidas</Form.Label><Form.Control type="text" value={config.titulo_faq} onChange={(e) => handleChange(servicoId, 'titulo_faq', e.target.value)} /></Form.Group>
                    {config.lista_faq.map((item, index) => (
                        <Card key={index} className="mb-2">
                            <Card.Body>
                                <Row>
                                    <Col>
                                        <Form.Control className="mb-2" type="text" placeholder="Pergunta" value={item.pergunta} onChange={(e) => handleListChange(servicoId, 'lista_faq', index, 'pergunta', e.target.value)} />
                                        <Form.Control as="textarea" rows={2} placeholder="Resposta" value={item.resposta} onChange={(e) => handleListChange(servicoId, 'lista_faq', index, 'resposta', e.target.value)} />
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center"><Button variant="danger" size="sm" onClick={() => removeListItem(servicoId, 'lista_faq', index)}><FontAwesomeIcon icon="trash" /></Button></Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                    <Button variant="outline-primary" size="sm" onClick={() => addListItem(servicoId, 'lista_faq')}><FontAwesomeIcon icon="plus" className="me-2" /> Adicionar Pergunta</Button>
                </Tab>
            )}
            {activeBlockTypes.has('antes_depois') && (
                <Tab eventKey="social" title="⭐ Prova Social">
                    <Form.Group className="mb-3"><Form.Label>Título da Seção "Antes e Depois"</Form.Label><Form.Control type="text" value={config.titulo_antes_depois} onChange={(e) => handleChange(servicoId, 'titulo_antes_depois', e.target.value)} /></Form.Group>
                    {config.lista_antes_depois.map((pair) => (
                        <Card key={pair.id} className="mb-3">
                            <Card.Body>
                                <Row className="align-items-center">
                                    <Col md={5}>
                                        <Form.Label>Antes</Form.Label>
                                        <Image src={pair.antes.preview || 'https://placehold.co/400x400/EFEFEF/333?text=Antes'} thumbnail className="mb-2" />
                                        <Form.Control type="file" accept="image/png, image/jpeg" onChange={(e) => handleImagePairChange(servicoId, pair.id, 'antes', e.target.files[0])} />
                                    </Col>
                                    <Col md={5}>
                                        <Form.Label>Depois</Form.Label>
                                        <Image src={pair.depois.preview || 'https://placehold.co/400x400/D1E7DD/333?text=Depois'} thumbnail className="mb-2" />
                                        <Form.Control type="file" accept="image/png, image/jpeg" onChange={(e) => handleImagePairChange(servicoId, pair.id, 'depois', e.target.files[0])} />
                                    </Col>
                                    <Col md={2} className="text-center"><Button variant="outline-danger" onClick={() => removeImagePair(servicoId, pair.id)}><FontAwesomeIcon icon="trash" /></Button></Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    ))}
                    <Button variant="outline-primary" size="sm" onClick={() => addImagePair(servicoId)}><FontAwesomeIcon icon="plus" className="me-2" /> Adicionar Par (Antes/Depois)</Button>
                </Tab>
            )}
            {activeBlockTypes.has('oferta') && (
                <Tab eventKey="oferta" title="💲 Oferta">
                    <Form.Group className="mb-3" controlId={`titulo_oferta-${servicoId}`}><Form.Label>Título da Seção de Oferta</Form.Label><Form.Control type="text" value={config.titulo_oferta} onChange={(e) => handleChange(servicoId, 'titulo_oferta', e.target.value)} /></Form.Group>
                    <Row>
                        <Col md={6}><Form.Group className="mb-3" controlId={`preco_antigo-${servicoId}`}><Form.Label>Preço Antigo (De)</Form.Label><Form.Control type="text" placeholder="Ex: R$ 1.500,00" value={config.preco_antigo} onChange={(e) => handleChange(servicoId, 'preco_antigo', e.target.value)} /></Form.Group></Col>
                        <Col md={6}><Form.Group className="mb-3" controlId={`preco_novo-${servicoId}`}><Form.Label>Preço Novo (Por)</Form.Label><Form.Control type="text" placeholder="Ex: R$ 1.299,00" value={config.preco_novo} onChange={(e) => handleChange(servicoId, 'preco_novo', e.target.value)} /></Form.Group></Col>
                    </Row>
                    <Form.Group className="mb-3" controlId={`texto_urgencia-${servicoId}`}><Form.Label>Texto de Urgência/Escassez</Form.Label><Form.Control as="textarea" rows={2} value={config.texto_urgencia} onChange={(e) => handleChange(servicoId, 'texto_urgencia', e.target.value)} /></Form.Group>
                </Tab>
            )}
            {activeBlockTypes.has('seo') && (
                <Tab eventKey="seo" title="⚙️ SEO e Metadados">
                    <Form.Group className="mb-3" controlId={`titulo_pagina-${servicoId}`}>
                        <Form.Label>Título da Página (SEO)</Form.Label>
                        <Form.Control type="text" value={config.titulo_pagina} onChange={(e) => handleChange(servicoId, 'titulo_pagina', e.target.value)} />
                        <Form.Text muted>Este é o título que aparece na aba do navegador e nos resultados do Google.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId={`meta_descricao-${servicoId}`}>
                        <Form.Label>Meta Descrição (SEO)</Form.Label>
                        <Form.Control as="textarea" rows={3} value={config.meta_descricao} onChange={(e) => handleChange(servicoId, 'meta_descricao', e.target.value)} />
                        <Form.Text muted>Descrição curta (máx. 160 caracteres) para os resultados de busca.</Form.Text>
                    </Form.Group>
                </Tab>
            )}
        </Tabs>
    );
};

export default LandPageServicosTab;
